<template>
    <!-- MidnightTarget shares this wrapper and keeps the old styling, so the
         refresh class only goes on when the weekly view is the one showing. -->
    <div class="weeklyTarget-wrapper"
        :class="titleType === 6 ? 'wt-midnightGap' : 'wt-refresh'">
        <div class="d-flex justify-content-center align-items-center weeklyTarget-header text-center">
            <span class="backArrowIcon" id="close" @click="viewClose('someStatus')">
                <img :src='downArrow' alt="Back Arrow" /></span>
            <!-- No v-if: the title used to wait for the status call, so the
                 header sat empty for the whole round trip on every refresh.
                 titleType, not statusType — it is remembered from the last
                 load, so a Mid Night host does not read "Weekly Target" first
                 and watch it change. Only the body waits for the real call. -->
            <h2 class="m-0">
                {{ titleType === 6 ? 'Mid Night Target' : 'Weekly Target' }}
            </h2>
        </div>
        <div v-if="!loading">
            <div v-if="statusType !== 6">
                <WeeklyTarget />
            </div>
            <div v-else>
                <MidnightTarget />
            </div>
        </div>
        <!-- The shape of what is coming, rather than the word "Loading": both
             views open on a ring hero followed by cards, so the page does not
             jump when the status call lands. -->
        <div v-else class="wt-skeleton">
            <div class="wt-skelCard">
                <div class="wt-skelHero">
                    <span class="sk sk-circle wt-skelRing"></span>
                    <div class="wt-skelLines">
                        <span class="sk" style="width: 44%; height: 9px"></span>
                        <span class="sk" style="width: 76%; height: 20px"></span>
                        <span class="sk" style="width: 58%; height: 11px"></span>
                    </div>
                </div>
                <span class="sk wt-skelNote"></span>
            </div>

            <div class="wt-skelCard">
                <span class="sk" style="width: 38%; height: 13px"></span>
                <div class="wt-skelBars">
                    <span v-for="n in 7" :key="n" class="sk"></span>
                </div>
            </div>

            <div class="wt-skelCard">
                <span class="sk" style="width: 46%; height: 13px"></span>
                <span class="sk" style="height: 56px; margin-top: 12px"></span>
            </div>
        </div>
    </div>

</template>
<script>
import { viewClose as androidViewClose } from '@/utils/android';
import axios from 'axios';
import '@/assets/css/skeleton.css';
import downArrow from '../../assets/images/back-blackArrow.svg';
import WeeklyTarget from './WeeklyTarget.vue'
import MidnightTarget from './MidnightTarget.vue'

/* The header title depends on the status call, but the header paints long
 * before that call returns. Remembering the last status lets the title be
 * right from the first frame on every refresh — which is when the Android app
 * reloads this page — instead of showing "Weekly Target" and swapping.
 *
 * Only the title reads this. The body still waits for the live status, so a
 * stale value can never render the wrong page. */
const TITLE_TYPE_KEY = 'weeklyTargetTitleType';

function readTitleType() {
    try {
        return Number(window.localStorage.getItem(TITLE_TYPE_KEY)) || null;
    } catch (error) {
        return null; // the WebView can have site data blocked
    }
}

function writeTitleType(value) {
    try {
        window.localStorage.setItem(TITLE_TYPE_KEY, String(value));
    } catch (error) {
        // nothing to do — the title just falls back to the default next time
    }
}

export default {
    name: 'TargetComponent',
    components: {
        WeeklyTarget,
        MidnightTarget
    },
    data() {
        return {
            statusType: null,
            titleType: readTitleType(),
            loading: true,
            downArrow,
        }
    },
    mounted() {
        this.fetchTargetStatusData();
    },
    methods: {
        async fetchTargetStatusData() {
            try {
                const tokenParam = this.$route.params.token;
                const token = tokenParam.split("=")[0];
                const apiUrl = `api/freetarget-weekly`;
                const response = await axios.get(apiUrl, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                // The endpoint returns two different shapes and the status
                // sits in a different place in each:
                //   midnight -> { success, result: {...}, status: 6, error }
                //   weekly   -> { success, result: { status: 3, ... }, error }
                // Read the top level first (that is the only place the
                // midnight shape carries it) and fall back to result.
                const payload = response.data || {};
                this.statusType = payload.status ?? payload.result?.status ?? null;
                this.titleType = this.statusType;
                writeTitleType(this.statusType);
            } catch (error) {
                console.error("Error fetching target status:", error);
            } finally {
                this.loading = false; // stop loader after API call
            }
        },
        // Close Webview on back arrow click
        viewClose(status) {
            if (window.Android && typeof window.Android.viewClose === "function") {
                androidViewClose(status);
            } // iOS interface via messageHandlers
            else if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.viewClose) {
                window.webkit.messageHandlers.viewClose.postMessage("close");
            } else {
                window.parent.postMessage({ action: "closePopup", status }, "*");
            }
        }
    }
}
</script>

<style scoped>
/* ---------- Loading skeleton ---------- */

.wt-skeleton {
    max-width: 430px;
    margin: 0 auto;
    padding: 0 14px;
}

.wt-skelCard {
    margin-bottom: 12px;
    padding: 20px 16px 16px;
    border-radius: 20px;
    background: #fff;
    box-shadow: 0 2px 10px rgba(19, 27, 35, 0.05);
}

.wt-skelHero {
    display: flex;
    align-items: center;
    gap: 18px;
}

.wt-skelRing {
    flex: 0 0 132px;
    width: 132px;
    height: 132px;
}

.wt-skelLines {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.wt-skelNote {
    height: 34px;
    margin-top: 16px;
    border-radius: 11px;
}

.wt-skelBars {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    gap: 5px;
    margin-top: 14px;
}

/* Uneven heights so it reads as a chart rather than a block. */
.wt-skelBars .sk { height: 72px; border-radius: 7px; }
.wt-skelBars .sk:nth-child(2) { height: 58px; }
.wt-skelBars .sk:nth-child(3) { height: 66px; }
.wt-skelBars .sk:nth-child(5) { height: 50px; }
.wt-skelBars .sk:nth-child(7) { height: 62px; }

/* Page chrome for the midnight branch. It matches WeeklyTargetRefresh.css
   exactly — the two views sit behind the same route and the same header, so
   the ground and the clearance must not change between them. The card styling
   itself lives in MidnightTargetRefresh.css.

   This has to be here rather than in that file because `.weeklyTarget-header`
   is a sibling of MidnightTarget's root, not a descendant of it. */
.wt-midnightGap {
    background: #F4F5F7;
    /* 50px status-bar gap + 14px + the 24px row + 14px, then a 12px breather
       before the first card — the same sum the weekly view uses. */
    padding-top: 114px;
    padding-bottom: 24px;
}

.wt-midnightGap .weeklyTarget-header {
    /* 50px up top: the Android WebView sits under the status bar, so the page
       keeps its own clearance rather than starting flush against it. */
    padding: 50px 14px 14px;
    margin-bottom: 0;
    box-shadow: 0 1px 0 rgba(19, 27, 35, 0.06);
}

.wt-midnightGap .weeklyTarget-header h2 {
    font-size: 18px;
    font-weight: 800;
}
</style>